import Errors from './Errors';

class Form {
    /**
     * Create a new Form instance.
     *
     * @param {object} data
     */
    constructor(data) {
        this.originalData = data;

        for (let field in data) {
            this[field] = data[field];
        }

        this.errors = new Errors();
    }

    /**
     * Fetch all relevant data for the form.
     */
    data() {
        let data = {};

        for (let property in this.originalData) {
            data[property] = this[property];
        }

        return data;
    }

    /**
     * Reset the form fields.
     */
    reset() {
        for (let field in this.originalData) {
            this[field] = '';
        }

        this.errors.clear();
    }

    /**
     * Send a POST request to the given URL.
     * .
     * @param {string} url
     */
    post(url) {
        return this.submit('post', url);
    }

    /**
     * Send a PUT request to the given URL.
     * .
     * @param {string} url
     */
    put(url) {
        return this.submit('put', url);
    }

    /**
     * Send a PATCH request to the given URL.
     * .
     * @param {string} url
     */
    patch(url) {
        return this.submit('patch', url);
    }

    /**
     * Send a DELETE request to the given URL.
     * .
     * @param {string} url
     */
    delete(url) {
        return this.submit('delete', url);
    }

    /**
     * Submit the form.
     *
     * @param {string} requestType
     * @param {string} url
     */
    submit(requestType, url) {
        return new Promise((resolve, reject) => {
            axios[requestType](url, this.data())
                .then(response => {
                    resolve(response.data);
                })
                .catch(async error => {
                    const status = error && error.response && error.response.status;

                    // On CSRF mismatch, fetch a fresh token, update the header, and retry once.
                    if (status === 419) {
                        try {
                            const { data: tokenData } = await axios.get('/csrf-token');
                            axios.defaults.headers.common['X-CSRF-TOKEN'] = tokenData.token;
                            const meta = document.head.querySelector('meta[name="csrf-token"]');
                            if (meta) meta.setAttribute('content', tokenData.token);

                            const retryResponse = await axios[requestType](url, this.data());
                            resolve(retryResponse.data);
                        } catch (retryError) {
                            const retryData = retryError && retryError.response ? retryError.response.data : null;
                            if (retryData) this.onFail(retryData);
                            reject(retryError);
                        }
                        return;
                    }

                    const data = error && error.response ? error.response.data : null;
                    // Only record validation errors when the server actually
                    // returned a body. Network errors, aborts (status_code 0),
                    // and timeouts have no payload and shouldn't reach onFail.
                    if (data) {
                        this.onFail(data);
                    }

                    reject(error);
                });
        });
    }

    /**
     * Handle a successful form submission.
     *
     * @param {object} data
     */
    onSuccess(data) {
        this.reset();
    }

    /**
     * Handle a failed form submission.
     *
     * @param {object} errors
     */
    onFail(errors) {
        this.errors.record(errors);
    }
}

export default Form;
